// Aircraft List Tab of the Aircraft Templates shell (InspectionsTREdit; legacy's
// name — this file was the "Limitations" tab until H98, 2026-09-28).
//
// BP-80 (Aaron 2026-10-06: "the same Link Aircraft (+)" as the inspection
// record): the shared RecordTabGrid, so (+) · ACTIONS live in the grid toolbar
// and there are no row icons — the inspection record's tab
// (src/inspections/InspectionComponent) has the same chrome. It was a bare
// DataGridPro with a per-row trash icon.
//
// Backed by three routes:
//   GET    /v6/inspection/aircraft-list/{inspection_id} -> list[InspectionAircraftListRow]
//   POST   /v6/inspection/aircraft-list/{inspection_id} -> the (+), LinkAircraftDialog
//   DELETE /v6/inspection/limitations/{limitation_id}   -> 204, soft (sets active=0)
//
// H98: the read moved from /limitations/{id} to /aircraft-list/{id}, which
// carries the airframe (`registration`) and legacy's merged warning colour and
// applies legacy's filter (no model-sourced rows, no retired airframes). What
// follows about the missing aircraft identifier is history: the column below
// now fills.
//
// (+) links airframes (POST above, crud.link_aircraft) — one row per airframe,
// the limitation pre-filled from the inspection, no Last Done recorded. There is
// still no PUT on this path; a row's limitation is edited from the aircraft.
//
// ENVELOPE: this route returns a BARE ARRAY (`response_model = list[...]`),
// unlike the paginated list route which returns Page[...] aliased to `data`.
// The service keeps the two straight — see inspections_tr.ts.
//
// EMPTY STATE: an inspection with no limitations renders the grid's own
// no-rows overlay. Nothing is seeded.
//
// ── THE ARCHETYPE: this is a FLEET-WIDE list, not a recurrence list ─────────
//
// One inspection DEFINITION, one row per AIRFRAME that carries it. Measured on
// g2 2026-09-20 (`module_g2_demo`), inspection 1 = "100 Hour Inspection":
//
//   SELECT COUNT(*), COUNT(DISTINCT aircraft_id) FROM aircraft_inspection_log
//    WHERE inspection_id=1 AND active=1;                    ->  5, 5
//
// Five rows, five DIFFERENT aircraft (146-149, 176 = C-GDMA..C-GDMD, C-GDNB) —
// NOT five recurrences of one inspection on one airframe. That misreading is
// the trap on this surface, because both stories produce a five-row grid.
//
// Legacy's equivalent tab is titled **"Aircraft List"** and leads with an
// Aircraft column (Legacy code/htdocs/mod_aircraft_templates/includes/tabs/
// aircraft_templates_tab_aircraft_list.inc:27-28, under `current_object is
// inspection`). We cannot lead with it: `schemas.InspectionLimitationList`
// omits every aircraft identifier. See types.ts:InspectionLimitation — the
// column is wired and renders an em dash until the backend adds the field.
//
// ⚠️ NOT THE AIRCRAFT'S Inspections tab. `/app/aircraft/:id` -> Inspections is
// the mirror-image surface (one airframe, many definitions) on a different
// route, `GET /v6/aircraft/inspections/{aircraft_id}`, whose schema joins the
// DEFINITION in instead. Two routes, opposite joins; conflating them is how a
// fix lands on the wrong grid.
import React, { useCallback, useMemo, useRef, useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import type { GridColDef, GridComparatorFn, GridSortDirection } from "@mui/x-data-grid-premium";
import { useSnackbar } from "notistack";

import AlertDialog from "../../components/AlertDialog";
import type { ActionMenuEntry } from "../../components/datagrid/ActionMenu";
import type { BasedOnChoice } from "../../components/LimitationFields";
import LinkAircraftDialog from "../dialogs/LinkAircraftDialog";
import {
  deleteInspectionLimitation,
  getInspectionAircraftList,
} from "../inspections_tr";
import type { InspectionAircraftListRow } from "../types";
import {
  limitationWarningLabel,
  limitationWarningRowClassName,
  limitationWarningRowSx,
} from "../limitationWarning";
import {
  isOverdue,
  remainingUnit,
  resolveRemaining,
} from "../limitationRemaining";
import { recordedDate } from "../recordedDate";
import RecordTabGrid, { columnSortValueOf } from "./RecordTabGrid";

interface Props {
  /** Undefined while the parent inspection is still unsaved (the create route). */
  inspectionId?: number;
}

// Decimal members arrive as JSON strings (Pydantic condecimal) — Number() them
// for the numeric columns so the grid sorts numerically, not lexically.
const num = (v: string | number | null | undefined) =>
  v === null || v === undefined || v === "" ? null : Number(v);

const dash = (label?: string) => (
  <Typography variant="body2" color="text.disabled" aria-label={label}>
    &mdash;
  </Typography>
);

// A recorded date, or null — NULL (a linked row, version-23) and the stored
// zero-date both mean "not recorded" and render "—" (Aaron 2026-10-06).
const dateColumn = (field: "last_date" | "next_date", headerName: string): GridColDef<InspectionAircraftListRow> => ({
  field,
  headerName,
  flex: 0.7,
  type: "date",
  valueGetter: (_v, row) => {
    const d = recordedDate(row[field]);
    return d ? new Date(d) : null;
  },
  renderCell: (p) => (p.value ? p.formattedValue : dash("Not recorded")),
});

/**
 * Remaining, CLOSEST FIRST (TICKET-22772, Skyline: "which aircraft is closest
 * to this inspection across the fleet"; Aaron ruled L0 for v6). Ascending is
 * closest-first: overdue (negative) rows lead, most overdue first, then the
 * least remaining. A row with no remaining (null — nothing recorded, an
 * on-condition axis) is LAST in both directions: it is not "far away", it is
 * unknown. Each row's figure stays on its own axis (days or units, per
 * `based_on`) — nothing is converted. `getSortComparator`, because MUI negates
 * a plain `sortComparator` for descending, which would put the unknowns first.
 */
export function remainingSortComparator(direction: GridSortDirection): GridComparatorFn<number | null> {
  const desc = direction === "desc";
  return (a, b) => {
    const aNone = a === null || a === undefined;
    const bNone = b === null || b === undefined;
    if (aNone || bNone) return aNone === bNone ? 0 : aNone ? 1 : -1;
    return desc ? b - a : a - b;
  };
}

/**
 * Remaining — shared by BOTH Aircraft List surfaces (this tab and the
 * /app/inspections record's, src/inspections/InspectionComponent.tsx): one
 * remaining rule (resolveRemaining), one rendering, one closest-first sort.
 */
export const REMAINING_COLUMN: GridColDef<InspectionAircraftListRow> = {
  // THE column legacy shows: ONE remaining, on whichever axis `based_on`
  // selects (limitationRemaining.ts). Stored values only — nothing computed
  // (AP-63/AP-64). Negative means OVERDUE and is rendered as such, never
  // clamped: g2 log row 178 (C-GDNB) carries units_remaining = -11.00.
  field: "__remaining",
  headerName: "Remaining",
  flex: 0.7,
  type: "number",
  filterable: false,
  // Sorts in the browser over the WHOLE list (RecordTabGrid `sortValueOf`);
  // `__remaining` is no column anywhere and never reaches a route.
  getSortComparator: remainingSortComparator,
  valueGetter: (_v, row) => resolveRemaining(row),
  renderCell: (p) => {
    if (p.value == null) return dash();
    const over = isOverdue(p.row);
    return (
      <Typography
        variant="body2"
        // Theme token, not a colour literal (PATTERNS.md BP-17). Never bold, and
        // otherwise the row's colour, so it reads like the rest of the row (Aaron
        // 2026-10-07: "black, red, blue amber no bold").
        sx={{ color: over ? "error.main" : "inherit", fontWeight: 400 }}
        aria-label={
          over
            ? `Overdue by ${Math.abs(p.value as number)} ${remainingUnit(p.row.based_on)}`
            : `${p.value} ${remainingUnit(p.row.based_on)} remaining`
        }
      >
        {p.value as number}
      </Typography>
    );
  },
};

// Columns mirror schemas.InspectionAircraftListRow member-for-member.
const COLUMNS: GridColDef<InspectionAircraftListRow>[] = [
  { field: "id", headerName: "#", flex: 0.3 },
  {
    // Legacy's Aircraft List leads with the airframe. `valueGetter` prefers the
    // joined registration and falls back to the bare id; neither = an em dash
    // that says so, never an invented airframe.
    field: "aircraft",
    headerName: "Aircraft",
    flex: 0.8,
    sortable: false,
    valueGetter: (_v, row) =>
      row.registration ?? (row.aircraft_id != null ? String(row.aircraft_id) : null),
    renderCell: (p) =>
      p.value != null ? (
        <span title={limitationWarningLabel(p.row)}>{p.value}</span>
      ) : (
        dash("Aircraft not reported")
      ),
  },
  { field: "based_on", headerName: "Based On", flex: 1.0 },
  REMAINING_COLUMN,
  dateColumn("last_date", "Last Date"),
  dateColumn("next_date", "Next Date"),
  { field: "last_units", headerName: "Last Units", flex: 0.6, type: "number", valueGetter: (_v, row) => num(row.last_units) },
  { field: "next_units", headerName: "Next Units", flex: 0.6, type: "number", valueGetter: (_v, row) => num(row.next_units) },
  { field: "period", headerName: "Period", flex: 0.5, type: "number", valueGetter: (_v, row) => num(row.period) },
  { field: "warning", headerName: "Warning", flex: 0.5, type: "number", valueGetter: (_v, row) => num(row.warning) },
  { field: "warning_unit", headerName: "Warning Unit", flex: 0.6 },
  { field: "extension", headerName: "Extension", flex: 0.5, type: "number", valueGetter: (_v, row) => num(row.extension) },
  { field: "days_remaining", headerName: "Days Left", flex: 0.5, type: "number" },
  { field: "units_remaining", headerName: "Units Left", flex: 0.5, type: "number", valueGetter: (_v, row) => num(row.units_remaining) },
  { field: "limitation_notes", headerName: "Notes", flex: 1.4 },
];

/** What each column shows = what it sorts on, over every row (RecordTabGrid). */
const sortValueOf = columnSortValueOf(COLUMNS);

export default function InspectionsTRLimitations({ inspectionId }: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const [refreshToken, setRefreshToken] = useState(0);
  const refresh = useCallback(() => setRefreshToken((t) => t + 1), []);
  const [linkOpen, setLinkOpen] = useState(false);
  const [deleting, setDeleting] = useState<InspectionAircraftListRow[]>([]);
  // Every row the last read returned (the grid holds one page): what (+) must
  // not offer again, and the Based On values this inspection already uses.
  const allRows = useRef<InspectionAircraftListRow[]>([]);

  const load = useCallback(async () => {
    const rows = await getInspectionAircraftList(inspectionId as number);
    allRows.current = rows;
    return rows;
  }, [inspectionId]);

  const actions = useMemo<ActionMenuEntry[]>(
    () => [
      {
        name: "Delete",
        namePlural: "Delete",
        action: ["Delete", "DELETE"],
        requiresSelection: true,
        multipleSelection: true,
        isReturnDataSelection: true,
        onClick: (payload: any) => {
          const rows: InspectionAircraftListRow[] = payload?.selectedRows ? Array.from(payload.selectedRows) : [];
          if (rows.length) setDeleting(rows);
        },
      },
    ],
    []
  );

  const confirmDelete = async () => {
    const rows = deleting;
    setDeleting([]);
    for (const row of rows) {
      try {
        await deleteInspectionLimitation(row.id);
        enqueueSnackbar("Limitation deleted", { variant: "success" });
      } catch (e: any) {
        const detail = e?.response?.data?.detail;
        enqueueSnackbar(typeof detail === "string" ? detail : "Couldn't delete limitation", { variant: "error" });
      }
    }
    refresh();
  };

  const linkedAircraftIds = useMemo(
    () => allRows.current.map((r) => r.aircraft_id).filter((id): id is number => !!id),
    // allRows is read when the dialog opens, after the grid has loaded
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [linkOpen]
  );
  const basedOnOptions = useMemo<BasedOnChoice[]>(
    () =>
      Array.from(new Set(allRows.current.map((r) => r.based_on).filter((v): v is string => !!v))).map((v) => ({
        value: v,
        label: v,
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [linkOpen]
  );

  // Create route: no id yet, so there is nothing to fetch against. Say so
  // plainly rather than rendering an empty grid that looks like "no data".
  if (!inspectionId) {
    return (
      <Box sx={{ p: "50px" }}>
        <Typography variant="body2" color="text.secondary">
          Save this inspection first — limitations attach to a saved record.
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <RecordTabGrid<InspectionAircraftListRow>
        name="inspections-tr-aircraft-list"
        what="limitations"
        testId="inspections-tr-aircraft-list-tab"
        load={load}
        columns={COLUMNS}
        noRowsLabel="No Limitation Added"
        // H98 — legacy colours the row's text by its (merged, stored) warning:
        // red overdue, blue coming due, green extension held.
        getRowClassName={(p) => limitationWarningRowClassName(p.row)}
        sx={limitationWarningRowSx}
        onCreate={() => setLinkOpen(true)}
        actions={actions}
        refreshToken={refreshToken}
        sortValueOf={sortValueOf}
      />
      <LinkAircraftDialog
        open={linkOpen}
        inspectionId={inspectionId}
        linkedAircraftIds={linkedAircraftIds}
        basedOnOptions={basedOnOptions}
        onClose={() => setLinkOpen(false)}
        onLinked={() => {
          setLinkOpen(false);
          refresh();
        }}
      />
      <AlertDialog
        title={deleting.length > 1 ? `Delete these ${deleting.length} limitations?` : "Delete this limitation?"}
        open={deleting.length > 0}
        handleClose={() => setDeleting([])}
      >
        <Button color="primary" onClick={() => setDeleting([])}>
          No
        </Button>
        <Button color="primary" autoFocus onClick={confirmDelete}>
          Yes
        </Button>
      </AlertDialog>
    </>
  );
}
